import React, { useCallback, useEffect, useMemo, useState } from "react";
import { Helmet } from "react-helmet";
import { Link, Redirect } from "react-router-dom";
import {
  communityPagesFromPayload,
  fetchCommunityNavigation,
  fetchCommunityPage,
} from "./communityNavigationApi";
import CommunityFeedPanel from "./CommunityFeedPanel";
import CommunityDirectoryGrid from "./CommunityDirectoryGrid";
import CommunityOrganizationTypeRows from "./CommunityOrganizationTypeRows";
import CommunitySocialLayout from "./CommunitySocialLayout";
import FilmFestivalPage from "../FilmFestival/FilmFestivalPage";
import {
  fetchOrganizationDirectory,
  fetchOrganizationDirectoryGrouped,
} from "../Helper/organizations";
import { fetchCreatorDirectory } from "../Helper/creators";
import { fetchProjectDirectory } from "../Helper/projects";
import "./CommunityPlaceholderPage.css";
import "./CommunitySocial.css";

const FEATURE_COPY = {
  social: {
    eyebrow: "Social",
    emptyTitle: "Share with the community",
    emptyBody:
      "Post updates, follow ministries and creators, and join the conversation.",
    statusLive: "Live",
  },
  directory: {
    eyebrow: "Directory",
    emptyTitle: "Browse organizations when they publish",
    emptyBody:
      "Public organization profiles will appear here as communities join the platform.",
    statusLive: "Directory",
  },
  organizations: {
    eyebrow: "Organizations",
    emptyTitle: "Organization directory coming online",
    emptyBody:
      "Discover ministries, schools, and partner organizations from this hub.",
    statusLive: "Directory",
  },
  creators: {
    eyebrow: "Creators",
    emptyTitle: "No creators published yet",
    emptyBody:
      "Public creator profiles will appear here as creators join the platform.",
    statusLive: "Directory",
  },
  projects: {
    eyebrow: "Projects",
    emptyTitle: "No projects published yet",
    emptyBody:
      "Collaborative projects and initiatives will surface here when communities launch them.",
    statusLive: "Directory",
  },
  events: {
    eyebrow: "Events",
    emptyTitle: "No upcoming events listed",
    emptyBody:
      "Gatherings, streams, and community events will appear in this space as they are scheduled.",
    statusLive: null,
  },
  film_festival: {
    eyebrow: "Film Festival",
    emptyTitle: "Film Festival",
    emptyBody:
      "Explore the live festival, Festival Pass, and programming from this Community page.",
    statusLive: "Live",
  },
  placeholder: {
    eyebrow: "Community",
    emptyTitle: "This space is reserved",
    emptyBody:
      "We are finishing a production-ready community experience for this section.",
    statusLive: null,
  },
};

const SLUG_FEATURE_MAP = {
  social: "social",
  organizations: "directory",
  directory: "directory",
  creators: "creators",
  projects: "projects",
  events: "events",
};

function resolveFeatureKey(page) {
  const type = (
    (page && (page.feature_type || "")) ||
    ""
  ).toLowerCase();
  if (type && type !== "placeholder") {
    if (type === "organizations") {
      return "directory";
    }
    if (type === "film_festival") {
      return "film_festival";
    }
    if (FEATURE_COPY[type] || type === "creators" || type === "projects") {
      return type;
    }
  }
  const slug = ((page && page.slug) || "").toLowerCase();
  if (slug === "film-festival" || slug === "filmfestival") {
    return "film_festival";
  }
  if (SLUG_FEATURE_MAP[slug]) {
    return SLUG_FEATURE_MAP[slug];
  }
  return "placeholder";
}

function resolveFeatureCopy(featureKey) {
  return FEATURE_COPY[featureKey] || FEATURE_COPY.placeholder;
}

function readTypeQuery(search) {
  if (!search) return null;
  try {
    const params = new URLSearchParams(search);
    const type = params.get("type");
    return type ? String(type).trim() : null;
  } catch (e) {
    return null;
  }
}

/**
 * Community page shell — directory/discovery when APIs exist; empty states otherwise.
 * Route: /community/:slug
 */
function CommunityPlaceholderPage(props) {
  const slug =
    (props.match && props.match.params && props.match.params.slug) ||
    props.slug ||
    "";
  const overrideTitle = props.title;
  const overrideDescription = props.description;
  const locationSearch =
    (props.location && props.location.search) ||
    (typeof window !== "undefined" ? window.location.search : "");
  const filterType = useMemo(() => readTypeQuery(locationSearch), [locationSearch]);

  const [page, setPage] = useState(null);
  const [siblings, setSiblings] = useState([]);
  const [loading, setLoading] = useState(true);
  const [notFound, setNotFound] = useState(false);
  const [loadError, setLoadError] = useState(false);

  const [directoryItems, setDirectoryItems] = useState([]);
  const [orgGroups, setOrgGroups] = useState([]);
  const [directoryLoading, setDirectoryLoading] = useState(false);
  const [directoryError, setDirectoryError] = useState(false);
  const [directoryReloadKey, setDirectoryReloadKey] = useState(0);

  useEffect(() => {
    if (overrideTitle) {
      setPage({
        title: overrideTitle,
        coming_soon_label: "Coming Soon",
        placeholder_description:
          overrideDescription ||
          "The Community experience is currently under development.",
        feature_type: "placeholder",
        slug: slug || "community",
      });
      setLoading(false);
      setNotFound(false);
      setLoadError(false);
      return undefined;
    }

    let cancelled = false;
    (async () => {
      setLoading(true);
      setNotFound(false);
      setLoadError(false);
      try {
        const [data, nav] = await Promise.all([
          fetchCommunityPage(slug),
          fetchCommunityNavigation().catch(() => null),
        ]);
        const payloadPage = (data && data.page) || data;
        if (!cancelled) {
          setPage(payloadPage);
          setSiblings(communityPagesFromPayload(nav));
          setLoading(false);
        }
      } catch (e) {
        if (!cancelled) {
          const message = (e && e.message) || "";
          const missing =
            /not found/i.test(message) ||
            message === "Unable to load community page";
          setNotFound(missing);
          setLoadError(!missing);
          setLoading(false);
          setPage(null);
        }
      }
    })();

    return () => {
      cancelled = true;
    };
  }, [slug, overrideTitle, overrideDescription]);

  const featureKey = resolveFeatureKey(page);
  const isFilmFestival = featureKey === "film_festival";
  const isDirectory = featureKey === "directory" || featureKey === "organizations";
  const isCreators = featureKey === "creators";
  const isProjects = featureKey === "projects";
  const isSocial = featureKey === "social";
  const usesDirectoryGrid = isCreators || isProjects;
  const usesOrgTypeRows = isDirectory;

  const reloadDirectory = useCallback(() => {
    setDirectoryReloadKey((k) => k + 1);
  }, []);

  useEffect(() => {
    if (loading || loadError || !page || !(usesDirectoryGrid || usesOrgTypeRows)) {
      return undefined;
    }
    let cancelled = false;
    (async () => {
      setDirectoryLoading(true);
      setDirectoryError(false);
      let items = [];
      let groups = [];
      let failed = false;

      if (isProjects) {
        const result = await fetchProjectDirectory({ per_page: 36 });
        items = result.projects || [];
        failed = !!result.error;
      } else if (isCreators) {
        const result = await fetchCreatorDirectory({ per_page: 36 });
        items = result.creators || [];
        failed = !!result.error;
      } else if (filterType) {
        const result = await fetchOrganizationDirectory({
          organization_type: filterType,
          per_page: 60,
        });
        items = result.organizations || [];
        failed = !!result.error;
        if (!failed && items.length) {
          const label =
            items[0].organization_type_label ||
            filterType.replace(/_/g, " ");
          groups = [
            {
              slug: filterType,
              type: filterType,
              label,
              name: label,
              count: items.length,
              has_more: false,
              organizations: items,
            },
          ];
        }
      } else {
        const result = await fetchOrganizationDirectoryGrouped({
          per_type: 12,
        });
        groups = result.groups || [];
        items = [];
        (groups || []).forEach((g) => {
          (g.organizations || []).forEach((o) => items.push(o));
        });
        failed = !!result.error;

        // Older backends may ignore `grouped` and return a flat list only.
        if ((!failed && groups.length === 0 && (result.organizations || []).length > 0) || failed) {
          const flat = failed
            ? await fetchOrganizationDirectory({ per_page: 48 })
            : {
                organizations: result.organizations || [],
                error: false,
              };
          if (!flat.error && (flat.organizations || []).length) {
            failed = false;
            items = flat.organizations || [];
            const byType = {};
            items.forEach((org) => {
              const key = org.organization_type || "other";
              if (!byType[key]) {
                byType[key] = {
                  slug: key,
                  type: key,
                  label: org.organization_type_label || key,
                  name: org.organization_type_label || key,
                  count: 0,
                  has_more: false,
                  organizations: [],
                };
              }
              byType[key].organizations.push(org);
              byType[key].count += 1;
            });
            groups = Object.keys(byType).map((k) => byType[k]);
          }
        }
      }

      if (!cancelled) {
        setDirectoryItems(items);
        setOrgGroups(groups);
        setDirectoryError(failed);
        setDirectoryLoading(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [
    loading,
    loadError,
    page,
    usesDirectoryGrid,
    usesOrgTypeRows,
    isProjects,
    isCreators,
    filterType,
    directoryReloadKey,
    slug,
  ]);

  if (notFound) {
    return <Redirect to="/home" />;
  }

  const festivalEditionSlug =
    page && page.meta && page.meta.edition_slug
      ? String(page.meta.edition_slug)
      : "";

  const title = (page && (page.title || page.name)) || "";
  const description =
    (page && (page.placeholder_description || page.description)) ||
    "The Community experience is currently under development.";
  const featureCopy = resolveFeatureCopy(featureKey);
  const hasLiveSurface =
    usesDirectoryGrid || usesOrgTypeRows || isSocial || isFilmFestival;
  const statusLabel = hasLiveSurface
    ? featureCopy.statusLive || "Live"
    : (page && page.coming_soon_label) || "Coming Soon";

  const directoryKindLabel = isProjects
    ? "Project"
    : isCreators
    ? "Creator"
    : "Organization";
  const directoryAria = isProjects
    ? "Project directory"
    : isCreators
    ? "Creator directory"
    : `${title || "Organization"} directory`;
  const directoryLiveTitle = isProjects
    ? "Browse projects"
    : isCreators
    ? "Browse creators"
    : filterType
    ? "Organizations in this type"
    : "Browse by organization type";
  const directoryLiveBody = isProjects
    ? "Support collaborative initiatives, follow progress, and contribute through internal donation checkout."
    : isCreators
    ? "Visit a creator profile to watch their latest videos and learn more."
    : "Rows follow the admin Organization Type order. Only types with published organizations are shown.";

  const orgCount =
    orgGroups.reduce(
      (sum, g) => sum + (Array.isArray(g.organizations) ? g.organizations.length : 0),
      0
    ) || directoryItems.length;

  return (
    <div className={"community-page" + (isSocial ? " community-page--social" : "")}>
      <Helmet>
        <title>{title ? `${title} — Community` : "Community"}</title>
      </Helmet>

      <header
        className="community-page__hero"
        aria-labelledby="community-page-title"
      >
        <div className="community-page__shell">
          <p className="community-page__eyebrow">Community</p>
          {loading ? (
            <div
              className="community-page__skeleton"
              role="status"
              aria-live="polite"
            >
              <span className="community-page__skeleton-line community-page__skeleton-line--lg" />
              <span className="community-page__skeleton-line" />
            </div>
          ) : (
            <>
              <h1 id="community-page-title" className="community-page__title">
                {title || "Community"}
              </h1>
              <p className="community-page__lede">
                {description || featureCopy.emptyBody}
              </p>
            </>
          )}
        </div>
      </header>

      {!loading && siblings.length > 1 ? (
        <nav
          className="community-page__subnav"
          aria-label="Community sections"
        >
          <div className="community-page__shell community-page__subnav-inner">
            {siblings.map((item) => {
              const itemSlug = item.slug || "";
              const path = item.path || `/community/${itemSlug}`;
              const isActive = itemSlug === slug;
              return (
                <Link
                  key={itemSlug || path}
                  to={path}
                  className={
                    "community-page__subnav-link" +
                    (isActive ? " is-active" : "")
                  }
                  aria-current={isActive ? "page" : undefined}
                >
                  {item.name || item.title || itemSlug}
                </Link>
              );
            })}
          </div>
        </nav>
      ) : null}

      <main className="community-page__main">
        <div className="community-page__shell">
          {loading ? (
            <div
              className="community-page__panel community-page__panel--loading"
              role="status"
              aria-live="polite"
            >
              <p className="community-page__loading">Loading community…</p>
            </div>
          ) : loadError ? (
            <div className="community-page__panel" role="alert">
              <h2 className="community-page__panel-title">Unable to load</h2>
              <p className="community-page__panel-body">
                We could not reach this community page right now. Try again
                shortly, or return home.
              </p>
              <div className="community-page__actions">
                <button
                  type="button"
                  className="btn btn-danger community-page__cta"
                  onClick={() => window.location.reload()}
                >
                  Retry
                </button>
                <Link
                  to="/home"
                  className="btn btn-outline-secondary community-page__cta"
                >
                  Back to Home
                </Link>
              </div>
            </div>
          ) : isSocial ? (
            <CommunitySocialLayout pageTitle={title || "Social"} />
          ) : isFilmFestival ? (
            <section
              className="community-page__panel community-page__panel--film-festival"
              aria-label={title || "Film Festival"}
            >
              <FilmFestivalPage
                embedded
                forcedSlug={festivalEditionSlug}
                history={props.history}
                location={props.location}
                match={{
                  params: { slug: festivalEditionSlug },
                  path: "/community/" + (slug || "film-festival"),
                  url: "/community/" + (slug || "film-festival"),
                }}
              />
            </section>
          ) : (
            <>
              <section
                className="community-page__panel"
                aria-labelledby="community-intro-title"
              >
                <p className="community-page__feature-label">
                  {featureCopy.eyebrow}
                </p>
                <p
                  className={
                    "community-page__status-pill" +
                    (hasLiveSurface
                      ? " community-page__status-pill--live"
                      : "")
                  }
                >
                  {statusLabel}
                </p>
                <h2
                  id="community-intro-title"
                  className="community-page__panel-title"
                >
                  {(usesDirectoryGrid || usesOrgTypeRows) &&
                  !directoryLoading &&
                  (directoryItems.length > 0 || orgGroups.length > 0)
                    ? directoryLiveTitle
                    : featureCopy.emptyTitle}
                </h2>
                <p className="community-page__panel-body">
                  {(usesDirectoryGrid || usesOrgTypeRows) &&
                  (directoryItems.length > 0 || orgGroups.length > 0)
                    ? directoryLiveBody
                    : featureCopy.emptyBody}
                </p>
                {usesOrgTypeRows && filterType ? (
                  <p className="community-page__section-more">
                    <Link to="/community/organizations">
                      ← All organization types
                    </Link>
                  </p>
                ) : null}
              </section>

              {usesOrgTypeRows ? (
                <section
                  className="community-page__directory-section"
                  aria-label={directoryAria}
                >
                  <CommunityOrganizationTypeRows
                    groups={orgGroups}
                    loading={directoryLoading}
                    error={directoryError}
                    emptyTitle={featureCopy.emptyTitle}
                    emptyBody={featureCopy.emptyBody}
                    onRetry={reloadDirectory}
                    filterType={null}
                  />
                  {!directoryLoading && !directoryError && orgCount > 0 ? (
                    <p className="community-page__section-more">
                      Showing {orgCount} organization
                      {orgCount === 1 ? "" : "s"} across{" "}
                      {orgGroups.filter((g) => (g.organizations || []).length).length}{" "}
                      type
                      {orgGroups.filter((g) => (g.organizations || []).length)
                        .length === 1
                        ? ""
                        : "s"}
                      .
                    </p>
                  ) : null}
                </section>
              ) : null}

              {usesDirectoryGrid ? (
                <section
                  className="community-page__directory-section"
                  aria-label={directoryAria}
                >
                  <CommunityDirectoryGrid
                    items={directoryItems}
                    loading={directoryLoading}
                    error={directoryError}
                    emptyTitle={featureCopy.emptyTitle}
                    emptyBody={featureCopy.emptyBody}
                    onRetry={reloadDirectory}
                    kindLabel={directoryKindLabel}
                  />
                </section>
              ) : null}

              {!usesDirectoryGrid && !usesOrgTypeRows && !isSocial ? (
                <section
                  className="community-page__feed-section"
                  aria-label={`${title || "Community"} feed`}
                >
                  <CommunityFeedPanel
                    featureType={featureKey}
                    title={`${title || "Community"} activity`}
                    items={[]}
                  />
                </section>
              ) : null}

              <aside className="community-page__related" aria-label="Related">
                <h2 className="community-page__related-title">
                  Keep exploring
                </h2>
                <ul className="community-page__related-list">
                  <li>
                    <Link to="/community">Community home</Link>
                  </li>
                  <li>
                    <Link to="/home">Home browse</Link>
                  </li>
                  <li>
                    <Link to="/wishlist">My Formation</Link>
                  </li>
                  <li>
                    <Link to="/platforms">Platform Availability</Link>
                  </li>
                  {isCreators ? (
                    <li>
                      <Link to="/community/organizations">
                        Organization directory
                      </Link>
                    </li>
                  ) : null}
                  {isDirectory || isCreators ? (
                    <li>
                      <Link to="/community/projects">Projects</Link>
                    </li>
                  ) : null}
                  {isProjects || isDirectory ? (
                    <li>
                      <Link to="/community/creators">Creators</Link>
                    </li>
                  ) : null}
                  <li>
                    <Link to="/community/social">Social</Link>
                  </li>
                </ul>
              </aside>
            </>
          )}
        </div>
      </main>
    </div>
  );
}

export default CommunityPlaceholderPage;
